import { LitElement, html, css } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { recordingManagerStore } from '../../systems/animation/RecordingManagerStore';
import { PlaybackController } from '../../systems/animation/PlaybackController';
import { TimelineRenderer } from '../../systems/animation/TimelineRenderer';

/**
 * Animation Timeline Panel
 * 
 * Advanced timeline visualization for animation debugging:
 * - Record live animation state
 * - Playback recorded sessions
 * - Scrub through timeline
 * - Export/import sessions
 */
@customElement('animation-timeline-panel')
export class AnimationTimelinePanel extends LitElement {
  @state() private selectedCharacter: string = '';
  @state() private recordingState: 'idle' | 'recording' | 'paused' = 'idle';
  @state() private playbackMode: boolean = false;
  @state() private playbackSpeed: number = 1.0;
  @state() private currentTime: number = 0;
  @state() private duration: number = 0;
  @state() private eventLog: string[] = [];

  @query('#timeline-canvas') private canvas!: HTMLCanvasElement;

  private recordingManager = recordingManagerStore.getRecordingManager();
  private playbackController = new PlaybackController();
  private timelineRenderer: TimelineRenderer | null = null;
  private animationFrameId: number = 0;

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      background: #1a1a1a;
      color: #fff;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px;
      background: #2a2a2a;
      border-bottom: 1px solid #444;
    }

    .header label {
      font-size: 12px;
      color: #aaa;
    }

    .header select {
      background: #333;
      color: #fff;
      border: 1px solid #555;
      padding: 4px 8px;
      border-radius: 3px;
      font-size: 12px;
    }

    .controls {
      display: flex;
      gap: 4px;
    }

    button {
      background: #444;
      color: #fff;
      border: 1px solid #666;
      padding: 6px 10px;
      border-radius: 3px;
      cursor: pointer;
      font-size: 12px;
      transition: background 0.2s;
    }

    button:hover {
      background: #555;
    }

    button:active {
      background: #333;
    }

    button.recording {
      background: #c00;
      border-color: #f00;
    }

    button.recording:hover {
      background: #d00;
    }

    button:disabled {
      background: #2a2a2a;
      color: #666;
      cursor: not-allowed;
    }

    .timeline-container {
      flex: 1;
      position: relative;
      overflow: hidden;
      min-height: 150px;
    }

    #timeline-canvas {
      width: 100%;
      height: 100%;
      display: block;
    }

    .scrubber {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px;
      background: #2a2a2a;
      border-top: 1px solid #444;
    }

    .scrubber input[type="range"] {
      flex: 1;
      height: 4px;
    }

    .scrubber-time {
      font-size: 11px;
      font-family: monospace;
      color: #aaa;
      min-width: 80px;
      text-align: right;
    }

    .playback-controls {
      display: flex;
      gap: 4px;
      align-items: center;
    }

    .speed-control {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-left: 8px;
    }

    .speed-control select {
      background: #333;
      color: #fff;
      border: 1px solid #555;
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 11px;
    }

    .event-log {
      background: #222;
      border-top: 1px solid #444;
      padding: 8px;
      max-height: 120px;
      overflow-y: auto;
      font-size: 11px;
      font-family: monospace;
    }

    .event-log-entry {
      padding: 2px 0;
      color: #aaa;
    }

    .event-log-entry.state-change {
      color: #ffa500;
    }

    .event-log-entry.blend {
      color: #ffff00;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    // Do not auto-start recording here; the game loop records into the shared store.
    this.recordingState = this.recordingManager.getState();
    this.startRenderLoop();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopRenderLoop();
  }

  firstUpdated() {
    if (this.canvas) {
      this.timelineRenderer = new TimelineRenderer(this.canvas);
      window.addEventListener('resize', () => this.handleResize());
    }
  }

  private startRenderLoop() {
    const render = () => {
      this.updateTimeline();
      this.animationFrameId = requestAnimationFrame(render);
    };
    this.animationFrameId = requestAnimationFrame(render);
  }

  private stopRenderLoop() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = 0;
    }
  }

  private updateTimeline() {
    if (!this.timelineRenderer) return;

    // Keep UI state in sync with the shared recorder.
    this.recordingState = this.recordingManager.getState();

    const frames = this.recordingManager.getFrames();
    if (frames.length === 0) return;

    const scrollingMode = this.recordingState === 'recording' || !this.playbackMode;
    this.timelineRenderer.render(frames, this.playbackController, scrollingMode);

    // Update current time display
    const state = this.playbackController.getState();
    this.currentTime = state.currentTime;
    
    if (frames.length > 0) {
      this.duration = frames[frames.length - 1].timestamp;
    }
  }

  private handleResize() {
    this.timelineRenderer?.resize();
  }

  private handleRecordToggle() {
    const state = this.recordingManager.getState();
    
    if (state === 'idle') {
      this.recordingManager.start(this.selectedCharacter || 'Player');
      this.recordingState = 'recording';
      this.addLogEntry('Started recording');
    } else if (state === 'recording') {
      this.recordingManager.pause();
      this.recordingState = 'paused';
      this.addLogEntry('Paused recording');
    } else if (state === 'paused') {
      this.recordingManager.resume();
      this.recordingState = 'recording';
      this.addLogEntry('Resumed recording');
    }
  }

  private handleStopRecording() {
    this.recordingManager.stop();
    this.recordingState = 'idle';
    this.addLogEntry('Stopped recording');
  }

  private handleClearRecording() {
    this.recordingManager.stop();
    this.playbackController.enterLiveMode();
    this.playbackMode = false;
    this.selectedCharacter = this.selectedCharacter; // Keep current selection
    this.eventLog = [];
    this.addLogEntry('Cleared recording');
  }

  private async handleExport() {
    const json = this.recordingManager.exportSession();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `animation-recording-${Date.now()}.json`;
    a.click();
    
    URL.revokeObjectURL(url);
    this.addLogEntry('Exported session');
  }

  private async handleImport() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.onchange = async (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      const text = await file.text();
      const success = this.recordingManager.importSession(text);
      
      if (success) {
        const frames = this.recordingManager.getFrames();
        this.playbackController.enterPlaybackMode(frames);
        this.playbackMode = true;
        this.addLogEntry(`Imported session (${frames.length} frames)`);
      } else {
        this.addLogEntry('Failed to import session');
      }
    };
    
    input.click();
  }

  private handlePlayPause() {
    const frames = this.recordingManager.getFrames();
    if (frames.length === 0) return;

    if (!this.playbackMode) {
      this.playbackController.enterPlaybackMode(frames);
      this.playbackMode = true;
    }
    
    this.playbackController.togglePlayPause();
    
    const state = this.playbackController.getState();
    this.addLogEntry(state.isPlaying ? 'Playing' : 'Paused');
  }

  private handleStepBackward() {
    if (!this.playbackMode) {
      const frames = this.recordingManager.getFrames();
      this.playbackController.enterPlaybackMode(frames);
      this.playbackMode = true;
    }
    
    this.playbackController.stepBackward();
    this.addLogEntry('Step backward');
  }

  private handleStepForward() {
    if (!this.playbackMode) {
      const frames = this.recordingManager.getFrames();
      this.playbackController.enterPlaybackMode(frames);
      this.playbackMode = true;
    }
    
    this.playbackController.stepForward();
    this.addLogEntry('Step forward');
  }

  private handleScrubberInput(e: Event) {
    const value = parseFloat((e.target as HTMLInputElement).value);
    
    if (!this.playbackMode) {
      const frames = this.recordingManager.getFrames();
      this.playbackController.enterPlaybackMode(frames);
      this.playbackMode = true;
    }
    
    this.playbackController.seekToNormalized(value);
  }

  private handleSpeedChange(e: Event) {
    const speed = parseFloat((e.target as HTMLSelectElement).value) as any;
    this.playbackController.setSpeed(speed);
    this.playbackSpeed = speed;
    this.addLogEntry(`Speed: ${speed}x`);
  }

  private handleFramerateChange(e: Event) {
    const framerate = parseInt((e.target as HTMLSelectElement).value) as any;
    this.recordingManager.setFramerate(framerate);
    this.addLogEntry(`Framerate: ${framerate} fps`);
  }

  private addLogEntry(message: string) {
    const timestamp = new Date().toLocaleTimeString();
    const entry = `[${timestamp}] ${message}`;
    this.eventLog = [...this.eventLog.slice(-50), entry]; // Keep last 50 entries
  }

  /**
   * Public API: Add animation frame (called from main.ts)
   */
  public addFrame(frame: { timestamp: number; currentState: string; state: any }) {
    recordingManagerStore.recordFrame({
      characterId: this.selectedCharacter || this.recordingManager.getCharacterId() || 'Player',
      debugState: frame.state,
      currentState: frame.currentState,
      currentPhase: null,
    });
  }

  render() {
    const playbackState = this.playbackController.getState();
    const progress = this.duration > 0 ? this.currentTime / this.duration : 0;

    return html`
      <div class="header">
        <label>Character:</label>
        <select @change=${(e: Event) => this.selectedCharacter = (e.target as HTMLSelectElement).value}>
          <option value="">All</option>
          <option value="daniel">Daniel</option>
          <option value="lilly">Lilly</option>
        </select>

        <div class="controls">
          <button 
            class="${this.recordingState === 'recording' ? 'recording' : ''}"
            @click=${this.handleRecordToggle}
          >
            ${this.recordingState === 'recording' ? '⏸' : '⏺'}
          </button>
          <button 
            @click=${this.handleStopRecording}
            ?disabled=${this.recordingState === 'idle'}
          >
            ⏹
          </button>
          <button @click=${this.handleClearRecording}>🗑️</button>
          <button @click=${this.handleExport} ?disabled=${this.recordingManager.getFrameCount() === 0}>💾</button>
          <button @click=${this.handleImport}>📂</button>
        </div>

        <div class="controls" style="margin-left: auto;">
          <label style="font-size: 11px;">Framerate:</label>
          <select @change=${this.handleFramerateChange}>
            <option value="30" selected>30 fps</option>
            <option value="60">60 fps</option>
            <option value="120">120 fps</option>
          </select>
        </div>
      </div>

      <div class="timeline-container">
        <canvas id="timeline-canvas"></canvas>
      </div>

      <div class="scrubber">
        <div class="playback-controls">
          <button @click=${this.handleStepBackward} ?disabled=${this.recordingManager.getFrameCount() === 0}>⏮</button>
          <button @click=${this.handlePlayPause} ?disabled=${this.recordingManager.getFrameCount() === 0}>
            ${playbackState.isPlaying ? '⏸' : '▶'}
          </button>
          <button @click=${this.handleStepForward} ?disabled=${this.recordingManager.getFrameCount() === 0}>⏭</button>
        </div>

        <input 
          type="range" 
          min="0" 
          max="1" 
          step="0.001"
          .value=${progress.toString()}
          @input=${this.handleScrubberInput}
          ?disabled=${this.recordingManager.getFrameCount() === 0}
        />

        <div class="scrubber-time">
          ${(this.currentTime / 1000).toFixed(2)}s / ${(this.duration / 1000).toFixed(2)}s
        </div>

        <div class="speed-control">
          <label style="font-size: 11px;">Speed:</label>
          <select @change=${this.handleSpeedChange} .value=${this.playbackSpeed.toString()}>
            <option value="0.1">0.1x</option>
            <option value="0.25">0.25x</option>
            <option value="0.5">0.5x</option>
            <option value="1.0" selected>1.0x</option>
            <option value="2.0">2.0x</option>
          </select>
        </div>
      </div>

      <div class="event-log">
        ${this.eventLog.map(entry => html`
          <div class="event-log-entry">${entry}</div>
        `)}
      </div>
    `;
  }
}
